/**
 * CÁ NHÂN.
 *
 * The hub for everything that is not "what am I doing right now". Keeping these
 * eleven destinations behind one tab is precisely what stops Trang chủ from
 * becoming the wall of buttons the spec warns against — each is one tap from
 * here, and none of them competes for attention on the home screen.
 */
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import { useHome } from '../lib/queries';
import { Button, Card, ConfirmDialog, MoneyText, SectionHeader, StatusBadge } from '../components/ui';
import { PRESENCE_LABEL, PRESENCE_TONE } from '../lib/labels';
import { useState } from 'react';

interface MenuItem {
  readonly to: string;
  readonly label: string;
  readonly hint: string;
  /** Hidden when the signed-in user lacks this permission. */
  readonly permission?: string;
}

const SECTIONS: readonly { title: string; items: readonly MenuItem[] }[] = [
  {
    title: 'Ca làm & chấm công',
    items: [
      { to: '/cham-cong', label: 'Chấm công', hint: 'Vào ca, tan ca, nghỉ giữa ca', permission: 'attendance.checkin' },
      { to: '/ca-nhan/lich-ca', label: 'Lịch ca', hint: 'Xem ca làm theo tuần', permission: 'shift.view.self' },
      {
        to: '/ca-nhan/lich-su-cham-cong',
        label: 'Lịch sử chấm công',
        hint: 'Từng lần chấm công và kết quả xác minh',
        permission: 'attendance.view.self',
      },
      { to: '/ca-nhan/xin-nghi', label: 'Xin nghỉ', hint: 'Gửi đơn nghỉ phép', permission: 'shift.leave.request' },
      {
        to: '/ca-nhan/doi-ca',
        label: 'Đổi ca / nhận ca trống',
        hint: 'Đổi ca với đồng nghiệp hoặc nhường ca',
        permission: 'shift.swap.request',
      },
    ],
  },
  {
    title: 'KPI & lương',
    items: [
      { to: '/ca-nhan/kpi', label: 'Chi tiết KPI', hint: 'Từng điểm cộng, điểm trừ và lý do', permission: 'kpi.view.self' },
      {
        to: '/ca-nhan/luong-tam-tinh',
        label: 'Lương tạm tính',
        hint: 'Bảng tính chi tiết kỳ lương đang mở',
        permission: 'payroll.view.self',
      },
      {
        to: '/ca-nhan/ung-luong',
        label: 'Ứng lương',
        hint: 'Gửi đề nghị tạm ứng',
        permission: 'payroll.advance.request',
      },
      {
        to: '/ca-nhan/khieu-nai',
        label: 'Khiếu nại KPI & chấm công',
        hint: 'Gửi và theo dõi khiếu nại',
        permission: 'kpi.appeal.create',
      },
    ],
  },
  {
    title: 'Khác',
    items: [{ to: '/ca-nhan/chat', label: 'Chat nội bộ', hint: 'Nhắn tin với ca làm' }],
  },
];

export function ProfileScreen(): JSX.Element {
  const navigate = useNavigate();
  const { me, branch, primaryRoleName, signOut, can } = useAuth();
  const home = useHome();
  const [confirmOut, setConfirmOut] = useState(false);
  const [signingOut, setSigningOut] = useState(false);

  return (
    <div className="space-y-3 pb-8">
      {/* --- profile ------------------------------------------------------ */}
      <Card>
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-lg font-bold text-slate-900">{me?.user.fullName ?? '—'}</p>
            <p className="text-sm text-slate-500">
              {primaryRoleName}
              {me?.user.employeeCode === null || me?.user.employeeCode === undefined
                ? ''
                : ` · Mã NV ${me.user.employeeCode}`}
            </p>
            <p className="mt-1 text-sm text-slate-500">
              {branch?.name ?? 'Chưa gán chi nhánh'} · {me?.tenant.name ?? ''}
            </p>
          </div>
          {home.data === undefined ? null : (
            <StatusBadge tone={PRESENCE_TONE[home.data.presence.status]}>
              {PRESENCE_LABEL[home.data.presence.status]}
            </StatusBadge>
          )}
        </div>

        {me !== null && me.roles.length > 1 ? (
          <p className="mt-3 border-t border-slate-100 pt-2 text-sm text-slate-600">
            Vai trò khác: {me.roles.slice(1).map((r) => r.nameVi).join(', ')}
          </p>
        ) : null}

        {home.data?.provisionalPay == null ? null : (
          <div className="mt-3 flex items-center justify-between border-t border-slate-100 pt-2">
            <span className="text-sm text-slate-600">Lương tạm tính kỳ này</span>
            <MoneyText
              amountMinor={home.data.provisionalPay.estimatedNetMinor}
              currency={home.data.provisionalPay.currency}
              className="font-bold"
            />
          </div>
        )}
      </Card>

      {/* --- menu --------------------------------------------------------- */}
      {SECTIONS.map((section) => {
        const visible = section.items.filter(
          (item) => item.permission === undefined || can(item.permission),
        );
        if (visible.length === 0) return null;
        return (
          <div key={section.title}>
            <SectionHeader title={section.title} />
            <Card className="!p-0">
              <ul className="divide-y divide-slate-100">
                {visible.map((item) => (
                  <li key={item.to}>
                    <button
                      type="button"
                      onClick={() => navigate(item.to)}
                      className="flex min-h-touch-lg w-full items-center justify-between gap-3 px-4 py-3 text-left active:bg-slate-50"
                    >
                      <span className="min-w-0">
                        <span className="block font-semibold text-slate-900">{item.label}</span>
                        <span className="block text-sm text-slate-500">{item.hint}</span>
                      </span>
                      <span aria-hidden="true" className="shrink-0 text-slate-400">
                        ›
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            </Card>
          </div>
        );
      })}

      {/* --- sign out ------------------------------------------------------ */}
      <div className="pt-4">
        <Button variant="danger" size="lg" fullWidth onClick={() => setConfirmOut(true)}>
          Đăng xuất
        </Button>
        <p className="mt-2 text-center text-xs text-slate-500">
          Phiên bản staff-app 0.1.0 · Thiết bị: {me?.session.deviceId ?? 'chưa đăng ký'}
        </p>
      </div>

      <ConfirmDialog
        open={confirmOut}
        title="Đăng xuất?"
        message="Các thao tác chưa đồng bộ sẽ vẫn nằm trên máy nhưng chỉ gửi được sau khi bạn đăng nhập lại. Hãy kiểm tra mục “Chưa đồng bộ” trước khi đăng xuất."
        confirmLabel="Đăng xuất"
        variant="danger"
        loading={signingOut}
        onCancel={() => setConfirmOut(false)}
        onConfirm={() => {
          setSigningOut(true);
          void signOut().finally(() => setSigningOut(false));
        }}
      />
    </div>
  );
}
